import { apiFetch } from "../utils/apiFetch";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { io } from "socket.io-client";
import { useNavigate } from "react-router";
import { useAuth } from "./AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { attachQuerySocketEvents } from "../utils/querySocketCache.mjs";

const SocketContext = createContext(null);

export const SocketProvider = ({ children, slug }) => {
  const [socket, setSocket] = useState(null);
  const [isSocketReady, setIsSocketReady] = useState(false);

  const navigate = useNavigate();
  const { userId, setUserId, setUserInfo, sessionVerified } = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!userId || !sessionVerified) {
      return;
    }

    const apiUrl = new URL(process.env.API_URL);

    const sock = io(apiUrl.origin, {
      path: `${apiUrl.pathname.replace(/\/$/, "")}/socket.io`,
      withCredentials: true,
      auth: {
        slug,
      },
    });

    let disposed = false;
    let connectedBefore = false;
    const prefix = ["account", userId];
    const detachQueryEvents = attachQuerySocketEvents(sock, queryClient, userId, () => navigate("/courses"));

    const refreshSubscription = async () => {
      try {
        const response = await apiFetch(`${process.env.API_URL}/api/userInfo`);

        if (!response.ok) {
          throw new Error("Could not refresh subscription");
        }

        const data = await response.json();
        if (!disposed) setUserInfo(data);
      } catch (error) {
        console.error("Subscription refresh failed:", error);
      }
    };

    const handleConnect = () => {
      setIsSocketReady(true);
      if (connectedBefore) queryClient.invalidateQueries({ queryKey: prefix });
      connectedBefore = true;
    };

    const handleDisconnect = reason => {
      setIsSocketReady(false);
      if (reason === "io server disconnect" && !disposed) {
        apiFetch(`${process.env.API_URL}/api/auth/me`).then(response => {
          if (response.ok && !disposed) sock.connect();
        }).catch(() => {});
      }
    };

    const handleConnectError = (error) => {
      if (disposed) return;
      setIsSocketReady(false);

      const message = String(error?.message || "Socket connection failed");
      if (error.data?.code === "SUBSCRIPTION_REQUIRED") {
        queryClient.clear();
        alert(message);
        navigate("/courses");
        return;
      }

      if (/invalid token|no token/i.test(message)) {
        window.dispatchEvent(new Event("auth:expired"));
        navigate("/");
        return;
      }

      if (message.toLowerCase().includes("access denied")) {
        queryClient.clear();
        alert(message);
        navigate("/courses");
        return;
      }

      console.error("Socket connection error:", message);
      console.log("description:", error.description);
      console.log("context:", error.context);
    };

    const subscriptionChanged = () => {
      refreshSubscription();
      queryClient.invalidateQueries({ queryKey: prefix });
    };
    sock.on("subscription:updated", subscriptionChanged);
    sock.on("connect", refreshSubscription);
    sock.on("connect", handleConnect);
    sock.on("disconnect", handleDisconnect);
    sock.on("connect_error", handleConnectError);

    setSocket(sock);

    return () => {
      disposed = true;
      detachQueryEvents();
      sock.off("subscription:updated", subscriptionChanged);
      sock.off("connect", refreshSubscription);
      sock.off("connect", handleConnect);
      sock.off("disconnect", handleDisconnect);
      sock.off("connect_error", handleConnectError);

      sock.disconnect();

      setSocket(null);
      setIsSocketReady(false);
    };
  }, [slug, navigate, userId, setUserId, setUserInfo, queryClient, sessionVerified]);

  const emitWithAck = useCallback(
    (event, payload = {}) =>
      new Promise((resolve, reject) => {
        if (!socket || !socket.connected) {
          reject(new Error("Socket not connected"));
          return;
        }

        socket.emit(event, payload, (response) => {
          if (!response?.ok) {
            reject(new Error(response?.error || "Socket request failed"));
            return;
          }
          resolve(response.data);
        });
      }),
    [socket],
  );

  return (
    <SocketContext.Provider
      value={{
        socket,
        isSocketReady,
        emitWithAck,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => {
  const context = useContext(SocketContext);

  if (!context) {
    throw new Error("useSocket must be used inside SocketProvider");
  }

  return context;
};
